import { TDeviceScreen } from '@/services';
import { getMobileRouteFromScreen } from '@/utils/device-route.util';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDeviceCheck } from './useDeviceCheck.hook';

/**
 * Gate a mobile screen against the `/device/client/check` response.
 *
 * Each MOB-xx page has an expected `screen` value:
 *   - MOB-01 accepts REGISTER, REJECTED_NOTICE, PENDING_NOTICE
 *   - MOB-02 accepts REGISTER, REJECTED_NOTICE
 *   - MOB-03 accepts PENDING_NOTICE
 *
 * If the server's response says otherwise, the guard redirects via
 * `getMobileRouteFromScreen`.
 *
 * Returns the underlying `useDeviceCheck()` result plus an `isReady` flag.
 * `isReady` stays false during the tick where the check has resolved but the
 * guard's effect is about to navigate away — callers use it to render a
 * loader instead of the page content, avoiding a flash of the wrong UI
 * before the redirect lands.
 */
export const useMobileScreenGuard = (
	expected: TDeviceScreen | TDeviceScreen[]
) => {
	const check = useDeviceCheck({ expectedDeviceType: 'mobile' });
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const { screen, isChecking, isLoading, error } = check;

	const expectedList = useMemo(
		() => (Array.isArray(expected) ? expected : [expected]),
		[expected]
	);

	// Will the effect below navigate away on the next tick? If so, we're not
	// "ready to render the page" yet — callers should keep the loader up.
	const willNavigate =
		!isChecking &&
		!isLoading &&
		!error &&
		!!screen &&
		!expectedList.includes(screen) &&
		getMobileRouteFromScreen(screen) !== pathname;

	const isReady = !isChecking && !isLoading && !willNavigate;

	useEffect(() => {
		// Wait for the check to finish before deciding.
		if (isChecking || isLoading) return;
		// On network / fingerprint error, stay put — DeviceRouteHandler already
		// falls back to MOB-01 on error, so nothing else to do.
		if (error) return;
		if (!screen) return;
		if (expectedList.includes(screen)) return;

		const target = getMobileRouteFromScreen(screen);
		if (target === pathname) return;

		navigate(target, { replace: true });
	}, [screen, isChecking, isLoading, error, expectedList, navigate, pathname]);

	return { ...check, isReady };
};

export default useMobileScreenGuard;
